
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { RecipeIdea, RecipeDifficulty, RecipeCategory } from "../types";
import { db } from "@/integrations/cloudflare/client";

export const useRecipeFetch = () => {
  const { toast } = useToast();
  const [recipes, setRecipes] = useState<RecipeIdea[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // For demo purposes, generate mock data
  const generateMockRecipes = () => {
    return [
      {
        id: '1',
        title: 'Gingerbread Cookies',
        description: 'Classic holiday gingerbread cookies perfect for decorating with the kids.',
        ingredients: [
          '3 cups all-purpose flour',
          '1 1/2 tsp baking powder',
          '3/4 tsp baking soda',
          '1/4 tsp salt',
          '1 tbsp ground ginger',
          '1 3/4 tsp ground cinnamon',
          '1/4 tsp ground cloves',
          '6 tbsp unsalted butter',
          '3/4 cup dark brown sugar',
          '1 large egg',
          '1/2 cup molasses',
          '2 tsp vanilla extract'
        ],
        instructions: '1. In a small bowl, whisk together the flour, baking powder, baking soda, salt, ginger, cinnamon, and cloves.\n\n2. In a large bowl using a hand-held mixer or stand mixer, beat the butter and brown sugar together until fluffy. Beat in the egg, molasses, and vanilla extract.\n\n3. Slowly mix in the dry ingredients until combined.\n\n4. Divide the dough into two equal parts. Wrap each in plastic wrap and chill for at least 3 hours.\n\n5. Preheat oven to 350°F (175°C).\n\n6. Roll out the dough on a floured surface to 1/4-inch thickness. Cut into shapes with cookie cutters.\n\n7. Place cookies on parchment-lined baking sheets.\n\n8. Bake for 8-10 minutes or until edges are just starting to brown.\n\n9. Cool on the baking sheet for 5 minutes, then transfer to a wire rack to cool completely.\n\n10. Decorate as desired with icing and sprinkles.',
        prepTime: '30 mins',
        cookTime: '10 mins',
        totalTime: '4 hours (including chilling)',
        servings: 24,
        difficulty: 'Medium' as RecipeDifficulty,
        category: 'Holiday Cookies' as RecipeCategory,
        tags: ['Christmas', 'Cookies', 'Dessert', 'Gingerbread'],
        kidFriendly: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '2',
        title: 'Hot Chocolate with Peppermint',
        description: 'Rich and creamy hot chocolate with a festive peppermint twist.',
        ingredients: [
          '4 cups milk',
          '1/4 cup cocoa powder',
          '1/4 cup granulated sugar',
          '1/2 cup semi-sweet chocolate chips',
          '1/2 tsp vanilla extract',
          '1/4 tsp peppermint extract',
          'Whipped cream for topping',
          'Crushed candy canes for garnish'
        ],
        instructions: '1. In a medium saucepan, whisk together the cocoa powder and sugar.\n\n2. Add milk and whisk until combined.\n\n3. Heat over medium heat, stirring occasionally, until the mixture begins to simmer.\n\n4. Add chocolate chips and stir until melted and smooth.\n\n5. Remove from heat and stir in vanilla and peppermint extracts.\n\n6. Pour into mugs and top with whipped cream and crushed candy canes.',
        prepTime: '5 mins',
        cookTime: '10 mins',
        totalTime: '15 mins',
        servings: 4,
        difficulty: 'Easy' as RecipeDifficulty,
        category: 'Drinks' as RecipeCategory,
        tags: ['Christmas', 'Peppermint', 'Hot Chocolate', 'Beverage'],
        kidFriendly: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '3',
        title: 'Roast Turkey with Herb Butter',
        description: 'A perfectly juicy and flavorful turkey with herb butter under the skin.',
        ingredients: [
          '1 whole turkey (12-14 lbs), thawed if frozen',
          '1 cup (2 sticks) unsalted butter, softened',
          '2 tbsp fresh rosemary, finely chopped',
          '2 tbsp fresh thyme leaves',
          '2 tbsp fresh sage, finely chopped',
          '4 garlic cloves, minced',
          '2 tsp salt',
          '1 tsp black pepper',
          '1 lemon, zested and quartered',
          '1 onion, quartered',
          '2 carrots, roughly chopped',
          '2 celery stalks, roughly chopped',
          '2 cups chicken broth'
        ],
        instructions: '1. Preheat oven to 325°F (165°C).\n\n2. In a bowl, mix the softened butter with herbs, garlic, lemon zest, salt, and pepper until well combined.\n\n3. Remove the turkey from packaging and remove the neck and giblets from the cavity. Pat the turkey dry with paper towels.\n\n4. Carefully loosen the skin over the breast and thighs. Spread about 2/3 of the herb butter underneath the skin, directly onto the meat.\n\n5. Rub the remaining butter over the outside of the turkey.\n\n6. Stuff the cavity with the quartered lemon and some of the onion, carrots, and celery.\n\n7. Place the remaining vegetables in the bottom of a large roasting pan and place the turkey on top, breast side up.\n\n8. Pour the chicken broth into the bottom of the pan.\n\n9. Roast for about 3-4 hours, or until a meat thermometer inserted into the thickest part of the thigh registers 165°F (74°C).\n\n10. Baste the turkey with the pan juices every 30 minutes after the first hour.\n\n11. If the turkey browns too quickly, cover loosely with aluminum foil.\n\n12. Let the turkey rest for at least 20-30 minutes before carving.',
        prepTime: '30 mins',
        cookTime: '3-4 hours',
        totalTime: '4-5 hours',
        servings: 8,
        difficulty: 'Hard' as RecipeDifficulty,
        category: 'Christmas Dinner' as RecipeCategory,
        tags: ['Christmas', 'Turkey', 'Main Dish', 'Holiday'],
        kidFriendly: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
    ];
  };

  // Fetch recipes on component mount
  useEffect(() => {
    const fetchRecipes = async () => {
      setIsLoading(true);
      try {
        // The holiday_recipes table should exist now after running the migration
        const { data, error } = await db
          .from('holiday_recipes')
          .select('*')
          .order('title');
        
        if (error) {
          throw error;
        }
        
        if (data && data.length > 0) {
          // Transform the data to match our RecipeIdea type
          const transformedData: RecipeIdea[] = data.map(recipe => ({
            id: recipe.id,
            title: recipe.title,
            description: recipe.description,
            ingredients: recipe.ingredients,
            instructions: recipe.instructions,
            prepTime: recipe.prep_time,
            cookTime: recipe.cook_time,
            totalTime: recipe.total_time,
            servings: recipe.servings,
            difficulty: recipe.difficulty as RecipeDifficulty,
            category: recipe.category as RecipeCategory,
            tags: recipe.tags,
            imageUrl: recipe.image_url,
            kidFriendly: recipe.kid_friendly,
            createdAt: recipe.created_at,
            updatedAt: recipe.updated_at
          }));
          
          setRecipes(transformedData);
        } else {
          // For demo purposes, generate mock data if no data exists
          setRecipes(generateMockRecipes());
        }
      } catch (error) {
        console.error('Error fetching recipes:', error);
        toast({
          title: "Error",
          description: "Failed to load recipes. Using sample data instead.",
          variant: "destructive",
        });
        
        // Use mock data as fallback
        setRecipes(generateMockRecipes());
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRecipes();
  }, [toast]);

  return { recipes, setRecipes, isLoading };
};
